import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

type FormState = {
  name: string;
  email: string;
  company: string;
  service: string;
  message: string;
};

const initialFormState: FormState = {
  name: '',
  email: '',
  company: '',
  service: '',
  message: '',
};

export default function ContactPage() {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormState>(initialFormState);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) {
      setError('プライバシーポリシーへの同意が必要です。');
      return;
    }

    const form = new FormData(e.currentTarget);
    const nextFormData: FormState = {
      name: String(form.get('name') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      company: String(form.get('company') ?? '').trim(),
      service: String(form.get('service') ?? '').trim(),
      message: String(form.get('message') ?? '').trim(),
    };

    if (!nextFormData.name || !nextFormData.email || !nextFormData.service || !nextFormData.message) {
      setError('必須項目を入力してください。');
      return;
    }

    setError('');
    setFormData(nextFormData);
    setConfirmOpen(true);
  };

  const handleConfirmedSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    setError('');

    const data = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));
    data.append('privacy', agreed ? '同意済み' : '');

    try {
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      const result = await response.text();
      if (!response.ok || result.trim() !== 'OK') {
        throw new Error('failed');
      }

      setSubmitted(true);
      setFormData(initialFormState);
      setAgreed(false);
    } catch {
      setError('送信に失敗しました。しばらく時間を置いてから再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />
      <section className="relative pt-36 pb-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #A3E635 100%)' }}>
        <div className="absolute top-8 right-16 w-40 h-40 bg-white/10 rounded-full" />
        <div className="absolute bottom-0 left-10 w-24 h-24 bg-white/10 rounded-full" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-black px-4 py-2 rounded-full mb-6 uppercase tracking-wider backdrop-blur-sm">📩 Contact</div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 drop-shadow-sm">お問い合わせ</h1>
          <p className="text-white/90 text-sm md:text-base max-w-xl mx-auto leading-relaxed font-medium">お気軽にお問い合わせください。担当者よりご連絡いたします。</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-black text-gray-900 mb-6">会社情報 🏢</h2>
              <div className="space-y-4">
                {[
                  { icon: 'ri-building-2-fill', label: '会社名', value: 'Grandeur Architect株式会社', color: '#38BDF8' },
                  { icon: 'ri-user-fill', label: '代表者', value: '山内　紫央里', color: '#F472B6' },
                  { icon: 'ri-map-pin-fill', label: '所在地', value: '〒530-0043 大阪市北区天満四丁目１４番２ー５０２号', color: '#FBBF24' },
                  { icon: 'ri-mail-fill', label: 'メール', value: 'info.customer@grandeur-architect.com', color: '#A3E635' },
                  { icon: 'ri-phone-fill', label: '電話番号', value: '050-8895-1393 / 080-2943-2049', color: '#FB923C' },
                  { icon: 'ri-calendar-fill', label: '設立', value: '令和3年7月21日', color: '#38BDF8' },
                  { icon: 'ri-briefcase-fill', label: '事業内容', value: '広告代理店業務及び各種の宣伝に関する業務', color: '#C084FC' },
                ].map((info) => (
                  <div key={info.label} className="flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: info.color + '20' }}>
                      <i className={`${info.icon} text-base`} style={{ color: info.color }} />
                    </div>
                    <div><p className="text-xs font-black mb-0.5" style={{ color: info.color }}>{info.label}</p><p className="text-sm font-medium text-gray-700">{info.value}</p></div>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-5 rounded-2xl" style={{ background: '#F3FFEA', border: '2px solid #A3E635' }}>
                <p className="text-sm font-black text-gray-900 mb-2">⏰ 営業時間</p>
                <p className="text-xs text-gray-600 leading-relaxed font-medium">月曜〜金曜：9:00〜18:00<br />土日・祝日休業</p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-black text-gray-700 mb-2">お名前 <span className="text-[#F472B6]">*</span></label>
                    <input type="text" name="name" required placeholder="山田 太郎" defaultValue={formData.name} className="w-full rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none transition-colors font-medium" style={{ border: '2px solid #E0F7FF' }} onFocus={(e) => e.target.style.borderColor='#38BDF8'} onBlur={(e) => e.target.style.borderColor='#E0F7FF'} />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-700 mb-2">メールアドレス <span className="text-[#F472B6]">*</span></label>
                    <input type="email" name="email" required placeholder="example@email.com" defaultValue={formData.email} className="w-full rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none transition-colors font-medium" style={{ border: '2px solid #FFF0F7' }} onFocus={(e) => e.target.style.borderColor='#F472B6'} onBlur={(e) => e.target.style.borderColor='#FFF0F7'} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-700 mb-2">会社名</label>
                  <input type="text" name="company" placeholder="株式会社〇〇" defaultValue={formData.company} className="w-full rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none font-medium" style={{ border: '2px solid #FFFBEB' }} onFocus={(e) => e.target.style.borderColor='#FBBF24'} onBlur={(e) => e.target.style.borderColor='#FFFBEB'} />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-700 mb-2">ご相談内容 <span className="text-[#F472B6]">*</span></label>
                  <select name="service" required defaultValue={formData.service} className="w-full rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none bg-white cursor-pointer font-medium" style={{ border: '2px solid #F3FFEA' }} onFocus={(e) => e.target.style.borderColor='#A3E635'} onBlur={(e) => e.target.style.borderColor='#F3FFEA'}>
                    <option value="">選択してください</option>
                    <option value="広告戦略・プランニング">広告戦略・プランニング</option>
                    <option value="ブランディング">ブランディング</option>
                    <option value="デジタルマーケティング">デジタルマーケティング</option>
                    <option value="PR・メディア対応">PR・メディア対応</option>
                    <option value="映像・動画制作">映像・動画制作</option>
                    <option value="クリエイティブ制作">クリエイティブ制作</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-700 mb-2">メッセージ <span className="text-[#F472B6]">*</span></label>
                  <textarea name="message" required rows={5} maxLength={500} placeholder="ご相談内容を詳しくお聞かせください（500文字以内）" defaultValue={formData.message} className="w-full rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none resize-none font-medium" style={{ border: '2px solid #E0F7FF' }} onFocus={(e) => e.target.style.borderColor='#38BDF8'} onBlur={(e) => e.target.style.borderColor='#E0F7FF'} />
                </div>
                <div className="rounded-2xl p-4" style={{ background: '#F8F9FF', border: '2px solid #E0E7FF' }}>
                  <div className="flex items-start gap-3">
                    <input
                      id="privacy-agreement"
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-5 w-5 cursor-pointer rounded border border-slate-300 accent-sky-500"
                    />
                    <label htmlFor="privacy-agreement" className="text-xs text-gray-600 leading-relaxed font-medium cursor-pointer">
                      <Link to="/privacy-policy" target="_blank" className="font-black underline cursor-pointer" style={{ color: '#38BDF8' }}>プライバシーポリシー</Link>を読み、内容に同意します。
                    </label>
                  </div>
                </div>
                {error && <p className="text-[#F472B6] text-xs font-bold">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full font-black py-4 rounded-full flex items-center justify-center gap-2 cursor-pointer transition-all hover:scale-105 disabled:opacity-60 whitespace-nowrap text-white" style={{ background: 'linear-gradient(135deg, #38BDF8, #F472B6)' }}>
                  {submitting ? <><i className="ri-loader-4-line animate-spin text-lg" />送信中...</> : <>入力内容を確認する <i className="ri-arrow-right-line text-lg" /></>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      {confirmOpen && (
        <div className="fixed inset-0 z-[60] bg-[#0A0F2E]/60 backdrop-blur-sm flex items-center justify-center px-6">
          <div className="w-full max-w-2xl bg-white rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[#38BDF8]/15 text-[#38BDF8]">
                <i className="ri-file-list-3-line text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-900">入力内容の確認</h2>
                <p className="text-sm text-gray-500 font-medium">この内容で送信する前にご確認ください。</p>
              </div>
            </div>
            <div className="space-y-4 mb-8">
              {[
                { label: 'お名前', value: formData.name },
                { label: 'メールアドレス', value: formData.email },
                { label: '会社名', value: formData.company || '（未入力）' },
                { label: 'ご相談内容', value: formData.service },
                { label: 'メッセージ', value: formData.message },
                { label: 'プライバシーポリシー', value: agreed ? '同意済み' : '未同意' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-gray-100 px-5 py-4">
                  <p className="text-xs font-black text-gray-500 mb-1">{item.label}</p>
                  <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 rounded-full py-3.5 font-black text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                修正する
              </button>
              <button
                type="button"
                onClick={handleConfirmedSubmit}
                className="flex-1 rounded-full py-3.5 font-black text-white whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #38BDF8, #F472B6)' }}
              >
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}
      {submitting && (
        <div className="fixed inset-0 z-[70] bg-[#0A0F2E]/60 backdrop-blur-sm flex items-center justify-center px-6">
          <div className="w-full max-w-md bg-white rounded-3xl p-8 text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center bg-[#38BDF8]/15 text-[#38BDF8]">
              <i className="ri-loader-4-line animate-spin text-3xl" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-3">送信中です</h2>
            <p className="text-sm text-gray-500 font-medium leading-relaxed">内容を送信しています。画面を閉じずにそのままお待ちください。</p>
          </div>
        </div>
      )}
      {submitted && (
        <div className="fixed inset-0 z-[60] bg-[#0A0F2E]/60 backdrop-blur-sm flex items-center justify-center px-6">
          <div className="w-full max-w-md bg-white rounded-3xl p-8 text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center bg-[#A3E635]/15 text-[#65A30D]">
              <i className="ri-checkbox-circle-fill text-3xl" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-3">送信完了</h2>
            <p className="text-sm text-gray-500 font-medium leading-relaxed mb-6">お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。</p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="w-full rounded-full py-3.5 font-black text-white whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #38BDF8, #F472B6)' }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
